import { useMemoize } from "@vueuse/core";
import { ApiClient } from "#/shared/api/client";

export const VISIT_TYPE_FILTER_DEFAULT_BASE_URL = "/physicians/filters?sections=visit_types";
export const VISIT_TYPE_FILTER_DEFAULT_API = "provider_search_v2_1";

/**
 * The visit-type options served by `filters.json+provider_search_v2_1.jbuilder`.
 * Unparsed: the facet reads `text`, `value`, `visible`, `parent` and `category` off
 * plain objects and builds its own item tree.
 */
export class VisitTypeFilterApi extends ApiClient {
  constructor(options) {
    super({ ...options, parse: false, root: "visit_types" });
  }
}

const clients = new Map();

function buildClient(url) {
  const instance = new VisitTypeFilterApi(
    url ? { baseURL: url } : { baseURL: VISIT_TYPE_FILTER_DEFAULT_BASE_URL, api: VISIT_TYPE_FILTER_DEFAULT_API },
  );

  // Keyed by the scope, so concurrent requests for one scope share a single response and a
  // repeated scope resolves from the cache. A failure is evicted, leaving the next call to retry.
  const fetchRows = useMemoize(async (scope) => (await instance.get("", scope ? { scope } : {})) ?? []);

  return {
    /**
     * Returns the visit-type options, narrowed to the providers a search scope
     * matches when one is given. A scope reaches the endpoint as Rails-nested
     * `scope[<filter>]=<value>` params.
     *
     * @param {Object} [params]
     * @param {Object} [params.scope] - Provider-search filters, e.g. `{ skill: "specialty-1", new_patient: "true" }`.
     * @returns {Promise<Array<Object>>} The option list, empty when the section is absent.
     */
    all: async ({ scope } = {}) => {
      try {
        return await fetchRows(scope);
      } catch (error) {
        fetchRows.delete(scope);

        throw error;
      }
    },
  };
}

/**
 * Returns the data source for the visit-type options. Every facet reading the same
 * endpoint shares one client, and with it one cache: the options depend on the scope
 * alone, and a page can mount the facet more than once.
 *
 * @param {Object} [options]
 * @param {String} [options.url] - Endpoint override. The default endpoint carries the API version.
 */
export const createVisitTypeFilterApi = (options = {}) => {
  const { url } = options;
  const existing = clients.get(url);

  if (existing) {
    return existing;
  }

  const client = buildClient(url);
  clients.set(url, client);

  return client;
};
